import { useEffect, useState, useRef, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Components
import Navbar from "@/components/Navbar";
import HeroSection from "@/components/HeroSection";
import StatsBar from "@/components/StatsBar";
import HowItWorks from "@/components/HowItWorks";
import IngredientsSection from "@/components/IngredientsSection";
import ShopSection from "@/components/ShopSection";
import ReviewsSection from "@/components/ReviewsSection";
import FAQSection from "@/components/FAQSection";
import CTABanner from "@/components/CTABanner";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import ThankYouPage from "@/components/ThankYouPage";
import CheckoutPage from "@/components/CheckoutPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [cart, setCart] = useState(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Refs for scroll
  const howItWorksRef = useRef(null);
  const formulaRef = useRef(null);
  const shopRef = useRef(null);
  const reviewsRef = useRef(null);
  const faqRef = useRef(null);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productRes, reviewsRes, faqsRes] = await Promise.all([
          axios.get(`${API}/product`),
          axios.get(`${API}/reviews`),
          axios.get(`${API}/faqs`)
        ]);
        
        setProduct(productRes.data);
        setReviews(reviewsRes.data);
        setFaqs(faqsRes.data);

        // Fire ViewContent pixel event for product
        if (window.fbq && productRes.data) {
          window.fbq('track', 'ViewContent', {
            content_name: productRes.data.name,
            content_ids: [productRes.data.id],
            content_type: 'product',
            value: productRes.data.price,
            currency: 'USD'
          });
        }
      } catch (e) {
        console.error("Error fetching data:", e);
        toast.error("Failed to load product data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Create cart on mount
  useEffect(() => {
    const initCart = async () => {
      // Check for existing cart in localStorage
      const existingCartId = localStorage.getItem("nukleus_cart_id");
      
      if (existingCartId) {
        try {
          const res = await axios.get(`${API}/cart/${existingCartId}`);
          setCart(res.data);
          return;
        } catch (e) {
          // Cart not found, create new one
          localStorage.removeItem("nukleus_cart_id");
        }
      }
      
      try {
        const res = await axios.post(`${API}/cart`);
        setCart(res.data);
        localStorage.setItem("nukleus_cart_id", res.data.id);
      } catch (e) {
        console.error("Error creating cart:", e);
      }
    };

    initCart();
  }, []);

  const addToCart = useCallback(async (quantity, isSubscription = false) => {
    if (!cart || !product) return;
    
    try {
      const res = await axios.post(`${API}/cart/${cart.id}/add`, {
        product_id: product.id,
        quantity,
        is_subscription: isSubscription
      });
      setCart(res.data);
      setIsCartOpen(true);
      toast.success(isSubscription ? "Subscription added to cart!" : "Added to cart!");

      // Fire AddToCart pixel event
      if (window.fbq) {
        window.fbq('track', 'AddToCart', {
          content_name: product.name,
          content_ids: [product.id],
          content_type: 'product',
          value: product.price * quantity * (isSubscription ? 0.85 : 1),
          currency: 'USD'
        });
      }
    } catch (e) {
      console.error("Error adding to cart:", e);
      toast.error("Failed to add to cart");
    }
  }, [cart, product]);

  const updateCartItem = useCallback(async (itemId, quantity) => {
    if (!cart) return;
    
    try {
      if (quantity <= 0) {
        const res = await axios.delete(`${API}/cart/${cart.id}/item/${itemId}`);
        setCart(res.data);
        toast.success("Item removed from cart");
      } else {
        const res = await axios.put(`${API}/cart/${cart.id}/item/${itemId}`, { quantity });
        setCart(res.data);
      }
    } catch (e) {
      console.error("Error updating cart:", e);
      toast.error("Failed to update cart");
    }
  }, [cart]);

  const checkout = useCallback(async () => {
    if (!cart) return;
    
    try {
      const res = await axios.post(`${API}/checkout/${cart.id}`);
      toast.success("Redirecting to checkout... (mocked)");
      // In production, redirect to res.data.checkout_url
      console.log("Checkout URL:", res.data.checkout_url);
    } catch (e) {
      console.error("Error during checkout:", e);
      toast.error(e.response?.data?.detail || "Checkout failed");
    }
  }, [cart]);

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: "smooth" });
  };

  const cartItemCount = cart?.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-nukleus-bg flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-nukleus-violet border-t-transparent rounded-full animate-spin" />
          <p className="text-white/50">Loading NUKLEUS...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-nukleus-bg overflow-x-hidden" data-testid="homepage">
      <Navbar 
        cartItemCount={cartItemCount}
        onCartClick={() => setIsCartOpen(true)}
        onNavClick={{
          howItWorks: () => scrollToSection(howItWorksRef),
          formula: () => scrollToSection(formulaRef),
          shop: () => scrollToSection(shopRef),
          reviews: () => scrollToSection(reviewsRef),
          faq: () => scrollToSection(faqRef)
        }}
      />
      
      <main>
        <HeroSection 
          product={product}
          onShopClick={() => scrollToSection(shopRef)}
          onScienceClick={() => scrollToSection(formulaRef)}
        />
        
        <StatsBar />
        
        <div ref={howItWorksRef}>
          <HowItWorks />
        </div>
        
        <div ref={formulaRef}>
          <IngredientsSection ingredients={product?.ingredients || []} />
        </div>
        
        <div ref={shopRef}>
          <ShopSection 
            product={product}
            onAddToCart={addToCart}
          />
        </div>
        
        <div ref={reviewsRef}>
          <ReviewsSection reviews={reviews} />
        </div>
        
        <div ref={faqRef}>
          <FAQSection faqs={faqs} />
        </div>
        
        <CTABanner onShopClick={() => scrollToSection(shopRef)} />
        
        <Footer />
      </main>

      <CartDrawer 
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cart={cart}
        onUpdateItem={updateCartItem}
        onCheckout={checkout}
      />
      
      <Toaster position="bottom-right" theme="dark" />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/thank-you" element={<ThankYouPage />} />
        </Routes>
        <Toaster position="bottom-right" theme="dark" />
      </BrowserRouter>
    </div>
  );
}

export default App;
